import i18n, { PostProcessorModule } from 'i18next'
import { initReactI18next } from 'react-i18next'
import Backend from 'i18next-chained-backend'
import HttpApi from 'i18next-http-backend'
import LocalStorageBackend from 'i18next-localstorage-backend'

import ms from 'ms'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/en'
import dayjs from 'dayjs'
import preferredOrg from '@utils/preferredOrg'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(localizedFormat)

const buildId = window.App?.buildId
const navigatorLocale = window.navigator?.language

export const DEFAULT_LOCALE = 'en-US'
export const DEFAULT_REGION = 'en'

export const locale = window.App?.preferredOrganization?.locale || navigatorLocale || DEFAULT_LOCALE
export const is12Hour = isNaN(+Intl.DateTimeFormat(locale, { hour: 'numeric' }).format())

dayjs.locale(locale)

export const SYSTEM_SUPPORTED_LANGUAGES = {
  'pt-BR': ['pt-BR'],
  'pt-PT': ['pt-PT'],
  'en-US': ['en-US'],
  'es-BO': ['es-BO'],
  'es-MX': ['es-MX'],
  'it-IT': ['it-IT'],
} as const

export type AvailableLanguages = keyof typeof SYSTEM_SUPPORTED_LANGUAGES

const supportedLngs = Object.values(SYSTEM_SUPPORTED_LANGUAGES).map((l) => l[0])

export const AVAILABLE_LOCALES = Object.keys(SYSTEM_SUPPORTED_LANGUAGES).map((locale) =>
  locale.toLowerCase(),
) as Lowercase<AvailableLanguages>[]

export const addOrganizationMarketSectorPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'addOrganizationMarketSector',
  process: (value, key, options, translator) => {
    const { marketSector } = preferredOrg()
    if (options.skipAppendMarketSector || !marketSector) return value

    const keyWithMarketSector = `${key}@${marketSector}`

    const translationWithMarketSectorExists = translator.exists(keyWithMarketSector)
    if (!translationWithMarketSectorExists) return value

    return translator.translate(keyWithMarketSector, { ...options, skipAppendMarketSector: true })
  },
}

i18n
  .use(initReactI18next)
  .use(Backend)
  .use(addOrganizationMarketSectorPostProcessor)
  .init({
    ns: ['common', 'errorsFrontend', 'errorsBackend', 'locales'],
    backend: {
      backends: [LocalStorageBackend, HttpApi],
      backendOptions: [
        {
          expirationTime: ms('3d'),
          defaultVersion: buildId,
        },
        {
          loadPath: `/assets/locales/{{lng}}/{{ns}}.json?buildId=${window.App?.buildId || Date.now()}`,
          requestOptions: {
            cache: 'no-cache',
          },
        },
      ],
    },
    fallbackLng: {
      ...SYSTEM_SUPPORTED_LANGUAGES,
      default: SYSTEM_SUPPORTED_LANGUAGES[DEFAULT_LOCALE],
    },
    lng: locale,
    interpolation: {
      escapeValue: false,
    },
    supportedLngs,
    postProcess: [addOrganizationMarketSectorPostProcessor.name],
  })

export default i18n

export type Translations = {
  [key: string]: string | string[] | Translations
}

export type TranslationsWithLanguageAndNamespace = Record<AvailableLanguages, Record<string, Translations>>
